import { useLoaderData, Link, useFetcher, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useEffect } from "react";
import { Header } from "../../components/layout/Header";
import { MemberList } from "../../components/organizations/MemberList";
import type { Member } from "../../components/organizations/MemberList";
import type { OrganizationDetailData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import { useAuth } from "../../hooks/useAuth";
import type { Role } from "../../types/user";
import "./OrganizationDetailPage.css";

export default function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { organization, members, teams } = useLoaderData() as OrganizationDetailData;
  const { user } = useAuth();
  const roleChangeFetcher = useFetcher<ActionResult>();
  const removeMemberFetcher = useFetcher<ActionResult>();

  const userRole = user?.organizations.find((o) => o.id === id)?.role ?? null;
  const canManageOrg = userRole === "owner" || userRole === "admin";

  const transformedMembers: Member[] = members.map((m) => ({
    id: m.userId,
    email: m.email,
    emailVerified: m.emailVerified,
    role: m.role,
    joinedAt: m.joinedAt,
  }));

  useEffect(() => {
    if (roleChangeFetcher.data && roleChangeFetcher.state === "idle") {
      if (roleChangeFetcher.data.success) {
        toast.success("Role updated successfully");
      } else if (roleChangeFetcher.data.error) {
        toast.error(roleChangeFetcher.data.error);
      }
    }
  }, [roleChangeFetcher.data, roleChangeFetcher.state]);

  useEffect(() => {
    if (removeMemberFetcher.data && removeMemberFetcher.state === "idle") {
      if (removeMemberFetcher.data.success) {
        toast.success("Member removed successfully");
      } else if (removeMemberFetcher.data.error) {
        toast.error(removeMemberFetcher.data.error);
      }
    }
  }, [removeMemberFetcher.data, removeMemberFetcher.state]);

  const handleRoleChange = async (userId: string, newRole: Role) => {
    roleChangeFetcher.submit(
      { role: newRole },
      {
        method: "post",
        action: `/organizations/${id}/members/${userId}/role`,
      },
    );
  };

  const handleRemoveMember = async (userId: string) => {
    if (!confirm("Are you sure you want to remove this member?")) return;

    removeMemberFetcher.submit(null, {
      method: "post",
      action: `/organizations/${id}/members/${userId}/remove`,
    });
  };

  return (
    <>
      <Header />
      <div className="container">
        <div className="org-detail-header">
          <div>
            <h1>{organization.name}</h1>
            <p className="org-slug">@{organization.slug}</p>
          </div>
          {userRole === "owner" && (
            <div className="header-actions">
              <Link to={`/organizations/${id}/billing`} className="button button-secondary">
                Billing
              </Link>
              <Link to={`/organizations/${id}/settings`} className="button button-secondary">
                Settings
              </Link>
            </div>
          )}
          {userRole === "admin" && (
            <Link to={`/organizations/${id}/settings`} className="button button-secondary">
              Settings
            </Link>
          )}
        </div>

        <div className="detail-grid">
          <div className="detail-section">
            <div className="section-header">
              <h2>Members ({transformedMembers.length})</h2>
              {canManageOrg && (
                <Link
                  to={`/organizations/${id}/members/invite`}
                  className="button button-small button-primary"
                >
                  Invite Member
                </Link>
              )}
            </div>
            <div className="members-list">
              {transformedMembers.length === 0 ? (
                <p className="empty-message">No members yet</p>
              ) : (
                <MemberList
                  members={transformedMembers}
                  currentUserRole={userRole || "member"}
                  organizationCreatorId={organization.createdBy}
                  onRoleChange={handleRoleChange}
                  onRemoveMember={handleRemoveMember}
                />
              )}
            </div>
          </div>

          <div className="detail-section">
            <div className="section-header">
              <h2>Teams ({teams.length})</h2>
              {canManageOrg && (
                <Link
                  to={`/organizations/${id}/teams/create`}
                  className="button button-small button-primary"
                >
                  Create Team
                </Link>
              )}
            </div>
            <div className="teams-list">
              {teams.length === 0 ? (
                <p className="empty-message">No teams yet</p>
              ) : (
                <div className="teams-grid">
                  {teams.map((team) => (
                    <Link
                      key={team.id}
                      to={`/organizations/${id}/teams/${team.id}`}
                      className="team-card"
                    >
                      <h3>{team.name}</h3>
                      {team.description && <p className="team-description">{team.description}</p>}
                      <p className="team-meta">
                        Created {new Date(team.createdAt).toLocaleDateString()}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
